import { useRef, useState } from "react";

import { Link } from "react-router-dom";
import { Box, Button, Card, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import type ECharts from "echarts-for-react";

import { useScrollReset } from "../../../../Components/hooks/useScrollReset";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { useTrackKubernetesLensEventOnce } from "../../events";
import { useCluster, useClusterMetrics } from "../../hooks";
import { clusterHasAgent } from "../../utils";
import { UtilizationCharts } from "./Charts";
import MultiWorkloadChart from "./Charts/MultiWorkloadChart";
import { Percentile } from "./Components/PercentileSelector";
import { TimeRange } from "./Components/TimeRangeSelector";
import { ClusterDetailDashboardContextProvider } from "./context";
import ClusterDetailHeader from "./Header";
import { WorkloadsTable } from "./WorkloadsTable";

export enum ChartId {
  MEMORY = "memory",
  CPU = "cpu",
  WORKLOADS = "workloads",
}

interface ClusterDetailPageProps {
  clusterId: string;
  initialTimeRange?: TimeRange;
}

const ClusterDetailPage = ({ clusterId, initialTimeRange }: ClusterDetailPageProps) => {
  const { customer } = useCustomerContext();

  const [percentile, setPercentile] = useState(Percentile.P_99_9);
  const [timeRange, setTimeRange] = useState(initialTimeRange ?? TimeRange.T_30D);
  const [cluster, clusterLoading] = useCluster(clusterId);

  const { metrics, loading: metricsLoading } = useClusterMetrics(cluster, timeRange, percentile);

  useTrackKubernetesLensEventOnce({
    name: "Cluster Detail Viewed",
    properties: {
      clusterId: cluster?.id ?? "",
      clusterType: cluster?.type ?? "",
    },
    condition: !!cluster,
  });

  const memoryChartRef = useRef<ECharts>(null);
  const cpuChartRef = useRef<ECharts>(null);
  const workloadsChartRef = useRef<ECharts>(null);

  const chartRefs = [
    { ref: memoryChartRef, id: ChartId.MEMORY },
    { ref: cpuChartRef, id: ChartId.CPU },
    { ref: workloadsChartRef, id: ChartId.WORKLOADS },
  ];

  // For some reason, the scroll position is not reset when navigating to this page, hiding the charts unless the user scrolls back up
  useScrollReset();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChartHighlight = (params: any, chartId: ChartId) => {
    const otherChartsRefs = chartRefs.filter((chart) => chart.id !== chartId);

    otherChartsRefs.forEach((chart) => {
      if (chart.ref.current) {
        chart.ref.current.getEchartsInstance().dispatchAction({
          type: "showTip",
          seriesIndex: params.batch?.[0].seriesIndex,
          dataIndex: params.batch?.[0].dataIndex,
        });
      }
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChartMouseOut = (params: any, chartId: ChartId) => {
    const otherChartsRefs = chartRefs.filter((chart) => chart.id !== chartId);

    otherChartsRefs.forEach((chart) => {
      if (chart.ref.current) {
        chart.ref.current.getEchartsInstance().dispatchAction({
          type: "hideTip",
        });
      }
    });
  };

  const getEventHandlers = (chartId: ChartId) => ({
    highlight: (params: object) => {
      handleChartHighlight(params, chartId);
    },
    mouseout: (params: object) => {
      handleChartMouseOut(params, chartId);
    },
    globalout: (params: object) => {
      handleChartMouseOut(params, chartId);
    },
  });

  if (!clusterLoading && !cluster) {
    return <Typography variant="h1">Cluster {clusterId} not found</Typography>;
  }

  const hasAgent = cluster ? clusterHasAgent(cluster) : false;

  return (
    <ClusterDetailDashboardContextProvider clusterId={clusterId} timeRange={timeRange} percentile={percentile}>
      <Box width="100%" my={3}>
        <Stack spacing={3}>
          <ClusterDetailHeader
            cluster={cluster}
            onChangePercentile={setPercentile}
            onChangeTimeRange={setTimeRange}
            percentile={percentile}
            timeRange={timeRange}
            loading={clusterLoading || metricsLoading}
            clusterHasAgent={hasAgent}
          />
          {hasAgent ? (
            <>
              <UtilizationCharts
                memoryChartRef={memoryChartRef}
                cpuChartRef={cpuChartRef}
                metrics={metrics ?? []}
                loading={metricsLoading}
                getEventHandlers={getEventHandlers}
              />
              <MultiWorkloadChart
                chartRef={workloadsChartRef}
                getEventHandlers={getEventHandlers}
                clusterId={clusterId}
              />
              <WorkloadsTable clusterId={clusterId} timeRange={timeRange} />
            </>
          ) : (
            <Card>
              <Stack spacing={2} p={4} alignItems="center">
                <Typography variant="h4" fontWeight={500}>
                  Install PerfectScale agent for utilization metrics
                </Typography>
                <Typography variant="body1">Analyze and report on workloads requests limits and utilization</Typography>
                <Button
                  component={Link}
                  to={`/customers/${customer.id}/integrate/assets/kubernetes/${clusterId}/agent/install`}
                  variant="contained"
                  color="primary"
                  sx={{ width: "fit-content" }}
                >
                  Install agent
                </Button>
              </Stack>
            </Card>
          )}
        </Stack>
      </Box>
    </ClusterDetailDashboardContextProvider>
  );
};

export default ClusterDetailPage;
